// Formatting helpers for the read-only detail screens (member, completed
// workout). Every helper takes whatever the Parse SDK hands back, including
// undefined for a field the row never had, and returns display text, so a
// missing or oddly typed field degrades one cell rather than the whole page.

export const EMPTY = '—'

// Fields that hold credentials. They reach the browser today, but there is no
// reason to paint them on a screen that gets shared in support calls, so the
// raw-field views only say whether one is set.
const SECRET_KEY_PATTERN = /token|secret|password/i

export const isSecretKey = (key) => SECRET_KEY_PATTERN.test(key || '')

export const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const pad2 = (value) => String(value).padStart(2, '0')

export const toDate = (value) => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  // Parse's JSON form of a date, e.g. inside an un-hydrated object field.
  if (typeof value === 'object' && value.iso) {
    return toDate(value.iso)
  }
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date
}

export const formatDateTime = (value) => {
  const date = toDate(value)
  if (!date) {
    return EMPTY
  }
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium'
  })
}

export const formatDate = (value) => {
  const date = toDate(value)
  if (!date) {
    return EMPTY
  }
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

export const formatUtcOffset = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) {
    return EMPTY
  }
  const sign = seconds < 0 ? '-' : '+'
  const abs = Math.abs(seconds)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.round((abs % 3600) / 60)
  return `UTC${sign}${pad2(hours)}:${pad2(minutes)}`
}

// The wall-clock time the swimmer saw, from the UTC offset their device
// recorded. Support reads "they swam at 7am" off this, not off the admin's own
// browser time zone.
export const formatSwimmerLocalTime = (value, secondsFromGMT) => {
  const date = toDate(value)
  if (!date || typeof secondsFromGMT !== 'number' || isNaN(secondsFromGMT)) {
    return EMPTY
  }
  const shifted = new Date(date.getTime() + secondsFromGMT * 1000)
  const text = shifted.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC'
  })
  return `${text} (${formatUtcOffset(secondsFromGMT)})`
}

export const formatDuration = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) {
    return EMPTY
  }
  const total = Math.round(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  if (hours > 0) {
    return `${hours}:${pad2(minutes)}:${pad2(rest)}`
  }
  return `${minutes}:${pad2(rest)}`
}

export const formatNumber = (value, maximumFractionDigits = 1) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return EMPTY
  }
  return value.toLocaleString(undefined, { maximumFractionDigits })
}

// One line for a collapsed section's header: the parts that have a value.
export const joinSummary = (parts) =>
  parts.filter((part) => !isBlank(part) && part !== EMPTY && part !== false)
    .join(' · ') || null

export const formatBool = (value) => {
  if (value === undefined || value === null) {
    return EMPTY
  }
  return value ? 'Yes' : 'No'
}

// Parse SDK values, told apart by shape so this stays testable without the SDK.
const isParseFile = (value) =>
  value && typeof value.url === 'function' && typeof value.name === 'function'

const isParseObject = (value) =>
  value && typeof value.className === 'string' && 'id' in value &&
  typeof value.get === 'function'

const isGeoPoint = (value) =>
  value && typeof value.latitude === 'number' &&
  typeof value.longitude === 'number' && typeof value.toJSON === 'function'

export const rawValueType = (value) => {
  if (value === undefined || value === null) return 'null'
  if (value instanceof Date) return 'date'
  if (Array.isArray(value)) return 'array'
  if (isParseFile(value)) return 'file'
  if (isParseObject(value)) return 'pointer'
  if (isGeoPoint(value)) return 'geopoint'
  return typeof value
}

const MAX_PREVIEW_LENGTH = 160

const truncate = (text) =>
  text.length > MAX_PREVIEW_LENGTH
    ? text.slice(0, MAX_PREVIEW_LENGTH) + '…'
    : text

// One-line preview of any stored value, for the raw-field table.
export const describeRawValue = (key, value) => {
  const type = rawValueType(value)
  if (type !== 'null' && isSecretKey(key)) {
    return 'set (hidden)'
  }
  switch (type) {
    case 'null':
      return 'null'
    case 'date':
      return toDate(value) ? value.toISOString() : 'Invalid date'
    case 'file':
      return value.name()
    case 'pointer':
      return `${value.className} ${value.id}`
    case 'geopoint':
      return `(${value.latitude}, ${value.longitude})`
    case 'array':
      return `${value.length} item${value.length === 1 ? '' : 's'}`
    case 'object':
      return truncate(safeStringify(value))
    case 'string':
      return truncate(value)
    default:
      return String(value)
  }
}

// Arrays and objects get a "view" toggle in the raw table; scalars don't.
export const isExpandableValue = (key, value) => {
  if (isSecretKey(key)) {
    return false
  }
  const type = rawValueType(value)
  return type === 'array' || type === 'object'
}

export const safeStringify = (value, space) => {
  try {
    return JSON.stringify(value, null, space)
  } catch (error) {
    return String(value)
  }
}

// Keys every Parse object has, which the raw table treats as already shown.
const BUILT_IN_KEYS = ['objectId', 'createdAt', 'updatedAt', 'ACL']

// Stored keys on a Parse object that the structured view above doesn't render.
export const unlistedKeys = (attributes, shownKeys) => {
  const shown = new Set([...BUILT_IN_KEYS, ...(shownKeys || [])])
  return Object.keys(attributes || {})
    .filter((key) => !shown.has(key))
    .sort()
}

// Header line for a collapsed "All fields" section. Fields the structured
// view doesn't render are the part worth noticing (a field the backend just
// started writing), so count those. `objects` is [parseObject, shownKeys][].
export const rawFieldsSummary = (objects) => {
  const count = objects.reduce((sum, [object, shownKeys]) =>
    sum + unlistedKeys(object?.attributes, shownKeys).length, 0)
  return count > 0
    ? `${count} field${count === 1 ? '' : 's'} not shown above`
    : null
}
